// The client can't read request headers, so "request comes from the 1romantic app"
// is detected by hostname (§13): the app WebView runs on app.1rom.ru. Dev hosts are
// allowed too so the feature is testable locally / over the tuna tunnel (mirrors the
// server-side dev domain-relax — Controller\Ai::isEligible()).
const APP_HOST = 'app.1rom.ru';
const DEV_HOSTS = ['1romantic', 'localhost', '127.0.0.1'];
const DEV_HOST_SUFFIX = '.tuna.am';

export function isInAppHost(hostname: string): boolean {
    const host = (hostname || '').toLowerCase();
    return (
        host === APP_HOST ||
        host.endsWith(DEV_HOST_SUFFIX) ||
        DEV_HOSTS.includes(host)
    );
}

export function isInApp(): boolean {
    // The app injects window.AppBridge on every WebView page → authoritative signal
    // (works on any 1romantic domain the WebView navigates to). Hostname is the
    // fallback for dev/local where the bridge isn't present but we want to test.
    return !!window.AppBridge || isInAppHost(window.location.hostname);
}

// The app's WebView only, without isInApp()'s dev hosts: the anonymous trial is
// web-only (Controller\Ai::anonymousTrialArm()) and must stay testable on dev.
export function isAppWebView(): boolean {
    return (
        !!window.AppBridge ||
        (window.location.hostname || '').toLowerCase() === APP_HOST
    );
}

// Best-effort client auth signal (UX only — the backend is the real gate). Keys off
// the credential cookie that logout deletes, NOT `auth_user_id` which the server
// intentionally keeps after sign-out (auth-lib/logout-link/const.ts).
export function hasAuthCookie(cookieString: string): boolean {
    return /(?:^|;\s*)auth_secret=[^;\s]/.test(cookieString || '');
}

export function isLoggedIn(): boolean {
    // Inside the app → trust the native bridge's auth state (the WebView has no
    // site cookies); on the web → the credential cookie (current scheme, kept for
    // the upcoming web rollout).
    const bridgeAuth = window.AppBridge?.auth;
    if (bridgeAuth) {
        return !!bridgeAuth.isAuthenticated;
    }
    return hasAuthCookie(document.cookie);
}

// Inside the app the WebView isn't cookie-authenticated, so the API call must carry
// the app's session id as X-Session (diCustomSession restores the session from it).
// On the web this is empty → cookie auth as usual.
export function sessionHeaders(): Record<string, string> {
    const sessionId = window.AppBridge?.auth?.sessionId;
    return sessionId ? { 'X-Session': sessionId } : {};
}

// Open sign-in for a logged-out user.
//  - In the app: ask the native app via the bridge to open its sign-in screen.
//  - On the web (no AppBridge): trigger the document-delegated `[data-purpose="auth"]`
//    handler (authLib.createAuth → showPopup). DOM event bubbling crosses webpack-bundle
//    boundaries; the `dip`/`Auth` globals (terser-scoped) do NOT. Kept for the web rollout.
export function requestSignIn(): void {
    if (window.AppBridge) {
        window.AppBridge.send('requestAuth', {});
        return;
    }

    const trigger = document.createElement('a');
    trigger.setAttribute('data-purpose', 'auth');
    trigger.style.display = 'none';
    document.body.appendChild(trigger);
    trigger.click();
    trigger.remove();
}
